import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  PerformanceAnalysisData,
  PerformanceAnalysisFilters,
  PerformanceAnalysisJobOption,
  PerformanceAnalysisQuery,
} from '../interfaces/performance-analysis.interface';
import {
  getMockPerformanceAnalysis,
  getMockPerformanceAnalysisCsv,
  getMockPerformanceAnalysisFilters,
  getMockPerformanceAnalysisPdf,
  USE_PERFORMANCE_ANALYSIS_MOCK,
} from '../modules/performance-analysis/mocks/performance-analysis.mock';

@Injectable({
  providedIn: 'root',
})
export class PerformanceAnalysisAdapter {
  private readonly http = inject(HttpClient);

  getFilters(): Observable<PerformanceAnalysisFilters> {
    if (USE_PERFORMANCE_ANALYSIS_MOCK) {
      return getMockPerformanceAnalysisFilters().pipe(
        map((filters) => this.normalizeFilters(filters))
      );
    }

    return this.http
      .get<PerformanceAnalysisFilters>(
        `${environment.apiBaseUrl}/company/performance-analysis/filters`
      )
      .pipe(map((filters) => this.normalizeFilters(filters)));
  }

  getPerformanceAnalysis(
    query: PerformanceAnalysisQuery
  ): Observable<PerformanceAnalysisData> {
    if (USE_PERFORMANCE_ANALYSIS_MOCK) {
      return getMockPerformanceAnalysis(query);
    }

    return this.http.get<PerformanceAnalysisData>(
      `${environment.apiBaseUrl}/company/performance-analysis`,
      { params: this.buildParams(query) }
    );
  }

  exportCsv(query: PerformanceAnalysisQuery): Observable<{ csv: string }> {
    if (USE_PERFORMANCE_ANALYSIS_MOCK) {
      return getMockPerformanceAnalysisCsv();
    }

    return this.http.get<{ csv: string }>(
      `${environment.apiBaseUrl}/company/performance-analysis/export/csv`,
      { params: this.buildParams(query) }
    );
  }

  exportPdf(query: PerformanceAnalysisQuery): Observable<Blob> {
    if (USE_PERFORMANCE_ANALYSIS_MOCK) {
      return getMockPerformanceAnalysisPdf();
    }

    return this.http.get(
      `${environment.apiBaseUrl}/company/performance-analysis/export/pdf`,
      {
        params: this.buildParams(query),
        responseType: 'blob',
      }
    );
  }

  private normalizeFilters(
    filters: PerformanceAnalysisFilters
  ): PerformanceAnalysisFilters {
    return {
      jobFilters: (filters.jobFilters ?? [])
        .map((option) => ({
          type: option.type,
          label: option.label ?? '',
        }))
        .filter((option) => !!option.type),
      jobs: (filters.jobs ?? [])
        .map((job) => this.normalizeJobOption(job))
        .filter((job): job is PerformanceAnalysisJobOption => job != null),
      periodOptions: (filters.periodOptions ?? [])
        .map((option) => ({
          type: option.type,
          label: option.label ?? '',
        }))
        .filter((option) => !!option.type),
    };
  }

  private normalizeJobOption(
    job: PerformanceAnalysisJobOption & { jobTitle?: string }
  ): PerformanceAnalysisJobOption | null {
    const id = job.id;
    const title = job.title ?? job.jobTitle;

    if (id == null || !title?.trim()) {
      return null;
    }

    return {
      id,
      title: title.trim(),
      status: job.status,
      slotType: job.slotType,
    };
  }

  private buildParams(query: PerformanceAnalysisQuery): HttpParams {
    let params = new HttpParams();

    if (query.jobFilter) {
      params = params.set('jobFilter', query.jobFilter);
    }
    if (query.jobId != null) {
      params = params.set('jobId', query.jobId);
    }
    if (query.period) {
      params = params.set('period', query.period);
    }
    if (query.startDate) {
      params = params.set('startDate', query.startDate);
    }
    if (query.endDate) {
      params = params.set('endDate', query.endDate);
    }

    return params;
  }
}
